import React, { useMemo, useState } from "react";
import {
  Box,
  Button,
  Divider,
  FormControlLabel,
  Paper,
  Stack,
  Switch,
  Typography,
  Alert,
  Snackbar,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Stepper,
  Step,
  StepLabel,
  List,
  ListItem,
  ListItemText,
  IconButton,
  Tooltip,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { NOTIFICATION_TYPES } from "../../notifications/types";
import { useNotifications } from "../../context/NotificationContext";
import { fetchPushVapidPublicKey, subscribeWebPush } from "../../api/client";

const GROUPS = [
  {
    key: "conv",
    labelKey: "notifications.groups.conversations",
    types: [NOTIFICATION_TYPES.chat_message, NOTIFICATION_TYPES.legacy_socket],
  },
  {
    key: "queue",
    labelKey: "notifications.groups.queue",
    types: [NOTIFICATION_TYPES.new_request],
  },
  {
    key: "assign",
    labelKey: "notifications.groups.assignments",
    types: [NOTIFICATION_TYPES.request_assigned],
  },
  {
    key: "sys",
    labelKey: "notifications.groups.system",
    types: [NOTIFICATION_TYPES.system, NOTIFICATION_TYPES.billing, NOTIFICATION_TYPES.security],
  },
];

function urlBase64ToUint8Array(base64String) {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

/** Preferências de alertas no perfil (inclui avançado + push). */
export default function NotificationPreferencesProfileSection() {
  const { t } = useTranslation("profile");
  const { prefs, setPrefs, requestDesktopPermission } = useNotifications();
  const { t: tn } = useTranslation("notifications");
  const [snack, setSnack] = useState({ open: false, message: "", severity: "info" });
  const [pushLoading, setPushLoading] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [wizardOpen, setWizardOpen] = useState(false);
  const [wizardMode, setWizardMode] = useState("desktop"); // desktop | push
  const [wizardStep, setWizardStep] = useState(0);
  const [wizardBusy, setWizardBusy] = useState(false);
  const [permissionsHelpOpen, setPermissionsHelpOpen] = useState(false);

  const notifPermission = typeof Notification !== "undefined" ? Notification.permission : "unsupported";

  const permissionLabel = useMemo(() => {
    const key =
      notifPermission === "granted" || notifPermission === "denied" || notifPermission === "default"
        ? notifPermission
        : "unsupported";
    return tn(`settings.permissionState.${key}`, { defaultValue: String(notifPermission || "") });
  }, [notifPermission, tn]);

  const steps = useMemo(
    () => [tn("permissionWizard.step1"), tn("permissionWizard.step2"), tn("permissionWizard.step3")],
    [tn]
  );

  const groupAllOn = (types) => types.every((ty) => prefs.categories[ty] !== false);

  const setGroup = (types, on) => {
    setPrefs((p) => {
      const categories = { ...p.categories };
      for (const ty of types) categories[ty] = on;
      return { ...p, categories };
    });
  };

  const toggleCategory = (key) => {
    setPrefs((p) => {
      const curOn = p.categories[key] !== false;
      return {
        ...p,
        categories: { ...p.categories, [key]: !curOn },
      };
    });
  };

  const handleWebPush = async () => {
    if (!("serviceWorker" in navigator) || !("PushManager" in window)) {
      setSnack({ open: true, message: tn("settings.webPushUnsupported"), severity: "warning" });
      return;
    }
    setPushLoading(true);
    try {
      await navigator.serviceWorker.register("/sw-push.js");
      const reg = await navigator.serviceWorker.ready;
      const publicKey = await fetchPushVapidPublicKey();
      if (!publicKey) throw new Error("no_vapid");
      const existing = await reg.pushManager.getSubscription();
      const sub =
        existing ||
        (await reg.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: urlBase64ToUint8Array(publicKey),
        }));
      await subscribeWebPush(sub.toJSON ? sub.toJSON() : sub);
      setSnack({ open: true, message: tn("settings.webPushOk"), severity: "success" });
    } catch (e) {
      console.error(e);
      setSnack({ open: true, message: tn("settings.webPushErr"), severity: "error" });
    } finally {
      setPushLoading(false);
    }
  };

  const openWizard = (mode) => {
    setWizardMode(mode);
    // Se já estiver permitido, pula direto para "Pronto" (mais claro e sem botão redundante).
    const perm = typeof Notification !== "undefined" ? Notification.permission : "unsupported";
    setWizardStep(perm === "granted" ? 2 : 0);
    setWizardBusy(false);
    setWizardOpen(true);
  };

  const closeWizard = () => {
    setWizardOpen(false);
  };

  const requestBrowserPermission = async () => {
    setWizardBusy(true);
    try {
      const r = await requestDesktopPermission();
      // requestDesktopPermission já marca desktopEnabled quando granted.
      if (r === "granted") {
        setWizardStep(2);
      } else {
        // denied ou default (fechou)
        setWizardStep(1);
      }
    } finally {
      setWizardBusy(false);
    }
  };

  const enablePushNow = async () => {
    setWizardBusy(true);
    try {
      if (typeof Notification !== "undefined" && Notification.permission !== "granted") {
        const r = await requestDesktopPermission();
        if (r !== "granted") return;
      }
      await handleWebPush();
      setWizardStep(2);
    } finally {
      setWizardBusy(false);
    }
  };

  const handleToggleDesktop = (checked) => {
    if (!checked) {
      setPrefs((p) => ({ ...p, desktopEnabled: false }));
      return;
    }
    if (typeof Notification === "undefined") {
      setSnack({ open: true, message: tn("settings.webPushUnsupported"), severity: "warning" });
      return;
    }
    if (Notification.permission === "granted") {
      setPrefs((p) => ({ ...p, desktopEnabled: true }));
      return;
    }
    openWizard("desktop");
  };

  return (
    <Paper id="profile-notifications" sx={{ p: 3, borderRadius: 3, height: "100%" }}>
      <Box sx={{ display: "flex", alignItems: "flex-start", gap: 0.5, mb: 0.5 }}>
        <Typography variant="h6" component="h2" sx={{ flex: 1, pr: 0.5 }}>
          {t("notifications.section")}
        </Typography>
        <Tooltip title={tn("permissionsHelp.iconTooltip")}>
          <IconButton
            size="small"
            aria-label={tn("permissionsHelp.iconAria")}
            onClick={() => setPermissionsHelpOpen(true)}
            sx={{ mt: -0.25 }}
          >
            <InfoOutlinedIcon fontSize="small" color="primary" />
          </IconButton>
        </Tooltip>
      </Box>
      <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2 }}>
        {t("notifications.hint")}
      </Typography>

      <Dialog
        open={permissionsHelpOpen}
        onClose={() => setPermissionsHelpOpen(false)}
        maxWidth="md"
        fullWidth
        scroll="paper"
        aria-labelledby="permissions-help-title"
      >
        <DialogTitle id="permissions-help-title">{tn("permissionsHelp.title")}</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2.5}>
            <Box>
              <Typography variant="subtitle1" fontWeight={800} gutterBottom>
                {tn("permissionsHelp.block1Title")}
              </Typography>
              <Typography variant="body2" color="text.secondary" paragraph sx={{ mb: 1 }}>
                {tn("permissionsHelp.block1p1")}
              </Typography>
              <Typography variant="body2" color="text.secondary" paragraph sx={{ mb: 1 }}>
                {tn("permissionsHelp.block1p2")}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {tn("permissionsHelp.block1p3")}
              </Typography>
            </Box>

            <Box>
              <Typography variant="subtitle1" fontWeight={800} gutterBottom>
                {tn("permissionsHelp.block2Title")}
              </Typography>
              <Typography variant="body2" color="text.secondary" paragraph sx={{ mb: 1 }}>
                {tn("permissionsHelp.block2p1")}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {tn("permissionsHelp.block2p2")}
              </Typography>
            </Box>

            <Box>
              <Typography variant="subtitle1" fontWeight={800} gutterBottom>
                {tn("permissionsHelp.block3Title")}
              </Typography>
              <Typography variant="body2" color="text.secondary" paragraph sx={{ mb: 1 }}>
                {tn("permissionsHelp.block3p1")}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {tn("permissionsHelp.block3p2")}
              </Typography>
            </Box>

            <Box>
              <Typography variant="subtitle1" fontWeight={800} gutterBottom>
                {tn("permissionsHelp.block4Title")}
              </Typography>
              <Typography variant="body2" color="text.secondary" paragraph sx={{ mb: 1 }}>
                {tn("permissionsHelp.block4p1")}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {tn("permissionsHelp.block4p2")}
              </Typography>
            </Box>

            <Box>
              <Typography variant="subtitle1" fontWeight={800} gutterBottom>
                {tn("permissionsHelp.block5Title")}
              </Typography>
              <Typography variant="body2" color="text.secondary" paragraph sx={{ mb: 1 }}>
                {tn("permissionsHelp.block5p1")}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {tn("permissionsHelp.block5p2")}
              </Typography>
            </Box>

            <Box>
              <Typography variant="subtitle1" fontWeight={800} gutterBottom>
                {tn("permissionsHelp.block6Title")}
              </Typography>
              <Typography variant="body2" color="text.secondary" paragraph>
                {tn("permissionsHelp.block6intro")}
              </Typography>
              <List dense disablePadding sx={{ pl: 2.5, listStyleType: "disc" }}>
                <ListItem disablePadding sx={{ display: "list-item", py: 0.25 }}>
                  <ListItemText primary={tn("permissionsHelp.block6itemConv")} primaryTypographyProps={{ variant: "body2" }} />
                </ListItem>
                <ListItem disablePadding sx={{ display: "list-item", py: 0.25 }}>
                  <ListItemText primary={tn("permissionsHelp.block6itemQueue")} primaryTypographyProps={{ variant: "body2" }} />
                </ListItem>
                <ListItem disablePadding sx={{ display: "list-item", py: 0.25 }}>
                  <ListItemText primary={tn("permissionsHelp.block6itemAssign")} primaryTypographyProps={{ variant: "body2" }} />
                </ListItem>
                <ListItem disablePadding sx={{ display: "list-item", py: 0.25 }}>
                  <ListItemText primary={tn("permissionsHelp.block6itemSys")} primaryTypographyProps={{ variant: "body2" }} />
                </ListItem>
              </List>
            </Box>

            <Box>
              <Typography variant="subtitle1" fontWeight={800} gutterBottom>
                {tn("permissionsHelp.block7Title")}
              </Typography>
              <Typography variant="body2" color="text.secondary" paragraph sx={{ mb: 1 }}>
                {tn("permissionsHelp.block7p1")}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {tn("permissionsHelp.block7p2")}
              </Typography>
            </Box>

            <Box>
              <Typography variant="subtitle1" fontWeight={800} gutterBottom>
                {tn("permissionsHelp.block8Title")}
              </Typography>
              <Typography variant="body2" color="text.secondary" paragraph sx={{ mb: 1 }}>
                {tn("permissionsHelp.block8p1")}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {tn("permissionsHelp.block8p2")}
              </Typography>
            </Box>

            <Box>
              <Typography variant="subtitle1" fontWeight={800} gutterBottom>
                {tn("permissionsHelp.block9Title")}
              </Typography>
              <Typography variant="body2" color="text.secondary" paragraph sx={{ mb: 1 }}>
                {tn("permissionsHelp.block9p1")}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {tn("permissionsHelp.block9p2")}
              </Typography>
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setPermissionsHelpOpen(false)} variant="contained" sx={{ textTransform: "none" }}>
            {tn("permissionsHelp.close")}
          </Button>
        </DialogActions>
      </Dialog>

      <Stack spacing={1.25}>
        <FormControlLabel
          control={
            <Switch
              checked={prefs.soundEnabled}
              onChange={(e) => setPrefs((p) => ({ ...p, soundEnabled: e.target.checked }))}
              color="primary"
            />
          }
          label={<Typography variant="body2">{t("notifications.sound")}</Typography>}
        />
        <FormControlLabel
          sx={{ ml: 0, pl: 1.5 }}
          control={
            <Switch
              size="small"
              checked={prefs.soundOnlyBackground}
              onChange={(e) => setPrefs((p) => ({ ...p, soundOnlyBackground: e.target.checked }))}
              disabled={!prefs.soundEnabled}
              color="primary"
            />
          }
          label={<Typography variant="body2">{t("notifications.soundBackground")}</Typography>}
        />

        <FormControlLabel
          control={
            <Switch
              checked={prefs.desktopEnabled}
              onChange={(e) => handleToggleDesktop(e.target.checked)}
              color="primary"
            />
          }
          label={<Typography variant="body2">{t("notifications.desktop")}</Typography>}
        />

        <Typography variant="subtitle2" color="text.secondary" sx={{ pt: 1 }}>
          {t("notifications.groupsTitle")}
        </Typography>
        {GROUPS.map((g) => (
          <FormControlLabel
            key={g.key}
            control={
              <Switch
                checked={groupAllOn(g.types)}
                onChange={(e) => setGroup(g.types, e.target.checked)}
                color="primary"
              />
            }
            label={<Typography variant="body2">{t(g.labelKey)}</Typography>}
          />
        ))}
      </Stack>

      <Box sx={{ mt: 2 }}>
        <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
          {t("notifications.autoSaveHint")}
        </Typography>
      </Box>

      <Divider sx={{ my: 2 }} />

      <Accordion expanded={expanded} onChange={(_, v) => setExpanded(v)} disableGutters>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="subtitle2" fontWeight={800}>
            {t("notifications.advancedTitle")}
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Typography color="text.secondary" variant="body2" sx={{ mb: 2 }}>
            {tn("settings.intro")}
          </Typography>

          <Stack spacing={1.5}>
            {typeof Notification !== "undefined" ? (
              <Alert severity="info" variant="outlined">
                <Typography fontWeight={800} sx={{ mb: 0.25 }}>
                  {tn("settings.permissionStatusTitle")}
                </Typography>
                <Typography variant="body2">
                  {tn("settings.permissionStatus", { status: permissionLabel })}
                </Typography>
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                  {tn("settings.permissionStatusHelp")}
                </Typography>
                {typeof Notification !== "undefined" && Notification.permission !== "granted" ? (
                  <Box sx={{ mt: 1 }}>
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={() => openWizard("desktop")}
                      sx={{ textTransform: "none", fontWeight: 700 }}
                    >
                      {t("notifications.permissionBrowser")}
                    </Button>
                  </Box>
                ) : null}
              </Alert>
            ) : null}

            <Typography variant="subtitle2" fontWeight={700}>
              {tn("settings.webPush")}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {tn("settings.webPushHelp")}
            </Typography>
            <Button
              variant="contained"
              onClick={() => {
                if (typeof Notification !== "undefined" && Notification.permission !== "granted") {
                  openWizard("push");
                  return;
                }
                void handleWebPush();
              }}
              disabled={pushLoading}
              sx={{ alignSelf: "flex-start", textTransform: "none", fontWeight: 800 }}
            >
              {tn("settings.webPushSubscribe")}
            </Button>

            <Divider />

            <FormControlLabel
              control={
                <Switch
                  checked={prefs.categories[NOTIFICATION_TYPES.legacy_socket] !== false}
                  onChange={() => toggleCategory(NOTIFICATION_TYPES.legacy_socket)}
                />
              }
              label={tn("settings.cat_legacy_socket")}
            />
          </Stack>
        </AccordionDetails>
      </Accordion>

      <Dialog
        open={wizardOpen}
        onClose={closeWizard}
        fullWidth
        maxWidth="sm"
        aria-labelledby="notification-permission-wizard-title"
      >
        <DialogTitle id="notification-permission-wizard-title">
          {wizardMode === "push"
            ? tn("permissionWizard.pushTitle")
            : tn("permissionWizard.desktopTitle")}
        </DialogTitle>
        <DialogContent dividers>
          <Stepper activeStep={wizardStep} sx={{ mb: 2 }}>
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>

          {wizardStep === 0 ? (
            <Alert severity="info" variant="outlined">
              {wizardMode === "push"
                ? tn("permissionWizard.introPush")
                : tn("permissionWizard.introDesktop")}
            </Alert>
          ) : null}

          {wizardStep === 1 ? (
            <>
              {notifPermission === "granted" ? (
                <Alert severity="success" variant="outlined" sx={{ mb: 2 }}>
                  {tn("permissionWizard.statusGranted")}
                </Alert>
              ) : notifPermission === "denied" ? (
                <>
                  <Alert severity="warning" variant="outlined" sx={{ mb: 2 }}>
                    <Typography fontWeight={800}>
                      {tn("permissionWizard.statusDeniedTitle")}
                    </Typography>
                    <Typography variant="body2" sx={{ mt: 0.5 }}>
                      {tn("permissionWizard.statusDeniedBody")}
                    </Typography>
                  </Alert>
                  <Typography variant="subtitle2" fontWeight={800} sx={{ mb: 1 }}>
                    {tn("permissionWizard.howToTitle")}
                  </Typography>
                  <List dense sx={{ m: 0 }}>
                    <ListItem sx={{ py: 0.25 }}>
                      <ListItemText primary={tn("permissionWizard.howTo1")} />
                    </ListItem>
                    <ListItem sx={{ py: 0.25 }}>
                      <ListItemText primary={tn("permissionWizard.howTo2")} />
                    </ListItem>
                    <ListItem sx={{ py: 0.25 }}>
                      <ListItemText primary={tn("permissionWizard.howTo3")} />
                    </ListItem>
                  </List>
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1.5 }}>
                    {tn("permissionWizard.helpNote")}
                  </Typography>
                </>
              ) : (
                <Alert severity="info" variant="outlined">
                  {tn("permissionWizard.statusDefault")}
                </Alert>
              )}
            </>
          ) : null}

          {wizardStep === 2 ? (
            <>
              <Alert severity="success" variant="outlined" sx={{ mb: 2 }}>
                {tn("permissionWizard.statusGranted")}
              </Alert>
              <Typography variant="subtitle2" fontWeight={800} sx={{ mb: 1 }}>
                {tn("permissionWizard.whatYouGetTitle")}
              </Typography>
              <List dense sx={{ m: 0 }}>
                <ListItem sx={{ py: 0.25 }}>
                  <ListItemText primary={tn("permissionWizard.whatYouGet1")} />
                </ListItem>
                <ListItem sx={{ py: 0.25 }}>
                  <ListItemText primary={tn("permissionWizard.whatYouGet2")} />
                </ListItem>
                <ListItem sx={{ py: 0.25 }}>
                  <ListItemText primary={tn("permissionWizard.whatYouGet3")} />
                </ListItem>
              </List>
            </>
          ) : null}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          {wizardStep === 0 ? (
            <Button
              variant="contained"
              onClick={() => setWizardStep(1)}
              sx={{ fontWeight: 800, textTransform: "none" }}
            >
              {tn("permissionWizard.ctaContinue")}
            </Button>
          ) : wizardStep === 2 ? (
            <Button
              variant="contained"
              onClick={closeWizard}
              sx={{ fontWeight: 800, textTransform: "none" }}
            >
              {tn("permissionWizard.ctaClose")}
            </Button>
          ) : wizardMode === "push" ? (
            <>
              <Button onClick={closeWizard} sx={{ textTransform: "none" }}>
                {tn("permissionWizard.ctaClose")}
              </Button>
              <Button
                variant="contained"
                disabled={wizardBusy || pushLoading || notifPermission === "denied"}
                onClick={enablePushNow}
                sx={{ fontWeight: 800, textTransform: "none" }}
              >
                {tn("permissionWizard.ctaEnablePush")}
              </Button>
            </>
          ) : (
            <>
              <Button onClick={closeWizard} sx={{ textTransform: "none" }}>
                {tn("permissionWizard.ctaClose")}
              </Button>
              <Button
                variant="contained"
                disabled={wizardBusy || notifPermission === "denied"}
                onClick={requestBrowserPermission}
                sx={{ fontWeight: 800, textTransform: "none" }}
              >
                {tn("permissionWizard.ctaRequest")}
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snack.open}
        autoHideDuration={5000}
        onClose={() => setSnack((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          severity={snack.severity}
          onClose={() => setSnack((s) => ({ ...s, open: false }))}
        >
          {snack.message}
        </Alert>
      </Snackbar>
    </Paper>
  );
}
